/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useId } from "react"
import type { FC, ReactElement } from "react"

import { CKEditor } from "@ckeditor/ckeditor5-react"
import eq from "lodash-es/eq"
import { useBoolean } from "react-use"

import { buildCssClassName } from "@/3514/utils"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import {
    ERichTextRendererFallbackSpinnerVariant as EFallbackSpinnerVariant,
    RichTextRendererFallbackSpinner as FallbackSpinner
} from "../components"
import { useCkRendererStylesheetLink } from "../hooks"
import { ClassicEditor, ckEditorConfig } from "../rich-text-renderer-config"
import { ERichTextRendererMode } from "../rich-text-renderer-types"

type TProps = { content: string; mode?: ERichTextRendererMode; onChange(content: string): void; isDisabled?: boolean }

/**
 * Only for RichTextRenderer. Potentially can be removed once there are no issues with renderers on prod
 * @param content string content to render
 * @param mode based on mode, toolbar and editing is active
 * @param onChange 'Edit' mode only! used for content state management
 * @param isDisabled 'Edit' mode only! used for disabling the input
 * @return ReactElement of Editor or Spinner if editor is not loaded
 */
const HTMLRenderer: FC<TProps> = ({ content, mode, onChange, isDisabled = false }: TProps): ReactElement => {
    const editorId: string = useId()

    const [isLayoutReady, setIsLayoutReady]: ReturnType<typeof useBoolean> = useBoolean(false)

    const handleReady: TEmptyCallback = useCallback((): void => setIsLayoutReady(true), [])

    const handleChange = useCallback(
        (_event, editor): void => {
            if (eq(mode, ERichTextRendererMode.Edit)) {
                const data: string = editor.getData()
                onChange(data)
            }
        },
        [mode, onChange]
    )

    useCkRendererStylesheetLink()

    return (
        <>
            {!isLayoutReady && (
                <FallbackSpinner
                    className="my-2"
                    variant={
                        eq(mode, ERichTextRendererMode.View)
                            ? EFallbackSpinnerVariant.Default
                            : EFallbackSpinnerVariant.Admin
                    }
                />
            )}
            <div className={buildCssClassName({ hidden: !isLayoutReady })}>
                <CKEditor
                    id={editorId}
                    disabled={eq(mode, ERichTextRendererMode.View) || isDisabled}
                    disableWatchdog={eq(mode, ERichTextRendererMode.View)}
                    editor={ClassicEditor}
                    config={ckEditorConfig as Required<object>}
                    data={content}
                    onReady={handleReady}
                    onChange={handleChange}
                    onError={emptyCallback}
                    onBlur={emptyCallback}
                    onFocus={emptyCallback}
                />
            </div>
        </>
    )
}

export { HTMLRenderer }
